import React, { useState, useEffect } from 'react';
import { settingsApi } from '../services/api';
import { SystemSettings } from '../types';
import { useAuth } from '../context/AuthContext';
import {
  Settings,
  Mail,
  Shield,
  CheckCircle,
  AlertCircle,
  Save,
  TestTube,
  Building,
  Server,
  QrCode,
  RefreshCw,
  FolderOpen,
  Copy,
  Eye,
  EyeOff
} from 'lucide-react';

interface FtpCredentials {
  enabled: boolean;
  localMode: boolean;
  host: string;
  port: number;
  user: string;
  password: string;
  inboxPath: string;
  archivePath: string;
  connectionInfo: {
    host: string;
    port: number;
    user: string;
    password: string;
    inboxPath: string;
    archivePath: string;
  };
}

export default function AdminSettings() {
  const { isSuperAdmin } = useAuth();
  const [settings, setSettings] = useState<SystemSettings | null>(null);
  const [ftpCredentials, setFtpCredentials] = useState<FtpCredentials | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('general');
  const [testEmail, setTestEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    loadSettings();
    loadFtpCredentials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSettings = async () => {
    try {
      const { settings } = await settingsApi.get();
      setSettings(settings);
    } catch (err) {
      setError('Fehler beim Laden der Einstellungen');
    } finally {
      setIsLoading(false);
    }
  };

  const loadFtpCredentials = async () => {
    try {
      const credentials = await settingsApi.getFtp();
      setFtpCredentials(credentials);
    } catch (err) {
      // Ignore - FTP might not be configured yet
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setSuccess(`${label} kopiert!`);
    setTimeout(() => setSuccess(''), 2000);
  };

  const handleSave = async () => {
    if (!settings) return;
    
    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      const { settings: updated } = await settingsApi.update(settings);
      setSettings(updated);
      setSuccess('Einstellungen gespeichert');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Speichern fehlgeschlagen');
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      setError('Bitte Test-E-Mail-Adresse eingeben');
      return;
    }

    try {
      await settingsApi.testEmail(testEmail);
      setSuccess('Test-E-Mail wurde gesendet');
    } catch (err: any) {
      setError(err.response?.data?.error || 'E-Mail-Test fehlgeschlagen');
    }
  };

  const handleTestATrust = async () => {
    try {
      const result = await settingsApi.testATrust();
      setSuccess(result.message);
    } catch (err: any) {
      setError(err.response?.data?.error || 'A-Trust-Test fehlgeschlagen');
    }
  };

  const updateSetting = (key: keyof SystemSettings, value: any) => {
    if (!settings) return;
    setSettings({ ...settings, [key]: value });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
        <p className="text-gray-600">Einstellungen konnten nicht geladen werden</p>
      </div>
    );
  }

  const tabs = [
    { id: 'general', label: 'Allgemein', icon: Settings },
    { id: 'email', label: 'E-Mail', icon: Mail },
    { id: 'atrust', label: 'A-Trust', icon: Shield },
    { id: 'ftp', label: 'Archiv-Robot', icon: Server },
    ...(isSuperAdmin ? [{ id: 'advanced', label: 'Erweitert', icon: Building }] : [])
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">System-Einstellungen</h1>
        <p className="mt-1 text-sm text-gray-500">
          Konfigurieren Sie E-Mail, A-Trust und weitere Systemoptionen
        </p>
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-700">
          <AlertCircle className="h-5 w-5 mr-2" />
          {error}
          <button onClick={() => setError('')} className="ml-auto">×</button>
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center text-green-700">
          <CheckCircle className="h-5 w-5 mr-2" />
          {success}
          <button onClick={() => setSuccess('')} className="ml-auto">×</button>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-6 py-4 text-sm font-medium border-b-2 ${
                    activeTab === tab.id
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-4 w-4 inline mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {/* Allgemein */}
          {activeTab === 'general' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h3 className="font-medium text-gray-900">Genehmigungsworkflows</h3>
                  <p className="text-sm text-gray-500">
                    Mehrstufige Genehmigungen vor der Unterschrift aktivieren
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.approvalWorkflowEnabled}
                    onChange={(e) => updateSetting('approvalWorkflowEnabled', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
              </div>
            </div>
          )}

          {/* E-Mail */}
          {activeTab === 'email' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h3 className="font-medium text-gray-900">E-Mail-Benachrichtigungen</h3>
                  <p className="text-sm text-gray-500">
                    Automatische Benachrichtigungen per E-Mail versenden
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.emailNotificationsEnabled}
                    onChange={(e) => updateSetting('emailNotificationsEnabled', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
              </div>

              {settings.emailNotificationsEnabled && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">SMTP Host</label>
                    <input
                      type="text"
                      value={settings.smtpHost || ''}
                      onChange={(e) => updateSetting('smtpHost', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="smtp.example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">SMTP Port</label>
                    <input
                      type="number"
                      value={settings.smtpPort || ''}
                      onChange={(e) => updateSetting('smtpPort', parseInt(e.target.value) || null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="587"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">SMTP Benutzer</label>
                    <input
                      type="text"
                      value={settings.smtpUser || ''}
                      onChange={(e) => updateSetting('smtpUser', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="user@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">SMTP Passwort</label>
                    <input
                      type="password"
                      value={settings.smtpPassword || ''}
                      onChange={(e) => updateSetting('smtpPassword', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="••••••••"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Absender-Adresse</label>
                    <input
                      type="email"
                      value={settings.smtpFrom || ''}
                      onChange={(e) => updateSetting('smtpFrom', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="noreply@example.com"
                    />
                  </div>

                  {/* Test E-Mail */}
                  <div className="col-span-2 pt-4 border-t border-gray-200">
                    <label className="block text-sm font-medium text-gray-700 mb-1">E-Mail-Konfiguration testen</label>
                    <div className="flex gap-2">
                      <input
                        type="email"
                        value={testEmail}
                        onChange={(e) => setTestEmail(e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                        placeholder="test@example.com"
                      />
                      <button
                        onClick={handleTestEmail}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center"
                      >
                        <TestTube className="h-4 w-4 mr-2" />
                        Test senden
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* A-Trust */}
          {activeTab === 'atrust' && (
            <div className="space-y-6">
              <div className="p-4 bg-blue-50 rounded-lg text-sm text-blue-700">
                <strong>Hinweis:</strong> Für die Integration mit A-Trust benötigen Sie einen gültigen API-Zugang.
                Kontaktieren Sie A-Trust für einen API-Schlüssel.
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h3 className="font-medium text-gray-900">A-Trust Integration</h3>
                  <p className="text-sm text-gray-500">
                    Qualifizierte elektronische Signaturen (QES) ermöglichen
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.aTrustEnabled}
                    onChange={(e) => updateSetting('aTrustEnabled', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
              </div>

              {settings.aTrustEnabled && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">A-Trust API URL</label>
                    <input
                      type="url"
                      value={settings.aTrustApiUrl || ''}
                      onChange={(e) => updateSetting('aTrustApiUrl', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="https://www.a-trust.at/api"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">A-Trust API Key</label>
                    <input
                      type="password"
                      value={settings.aTrustApiKey || ''}
                      onChange={(e) => updateSetting('aTrustApiKey', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="••••••••"
                    />
                  </div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">A-Trust Webhook Secret</label><input type="password" value={settings.aTrustWebhookSecret||''} onChange={e=>updateSetting('aTrustWebhookSecret',e.target.value)} className="w-full px-3 py-2 border rounded-lg" placeholder="••••••••"/></div>
                  <button
                    onClick={handleTestATrust}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center"
                  >
                    <TestTube className="h-4 w-4 mr-2" />
                    Verbindung testen
                  </button>
                </div>
              )}
              <div className="border-t pt-6 space-y-4">
                <div className="flex items-center justify-between"><div><h3 className="font-medium">Skribble</h3><p className="text-sm text-gray-500">Externe Signaturworkflows über die konfigurierte Skribble-API.</p></div><input aria-label="Skribble aktivieren" type="checkbox" checked={settings.skribbleEnabled||false} onChange={e=>updateSetting('skribbleEnabled',e.target.checked)}/></div>
                {settings.skribbleEnabled&&<><div><label className="block text-sm font-medium">Skribble API URL</label><input type="url" className="w-full px-3 py-2 border rounded-lg" value={settings.skribbleApiUrl||''} onChange={e=>updateSetting('skribbleApiUrl',e.target.value)}/></div><div><label className="block text-sm font-medium">Skribble API Key</label><input type="password" className="w-full px-3 py-2 border rounded-lg" value={settings.skribbleApiKey||''} onChange={e=>updateSetting('skribbleApiKey',e.target.value)}/></div><div><label className="block text-sm font-medium">Skribble Webhook Secret</label><input type="password" className="w-full px-3 py-2 border rounded-lg" value={settings.skribbleWebhookSecret||''} onChange={e=>updateSetting('skribbleWebhookSecret',e.target.value)}/></div></>}
              </div>
            </div>
          )}

          {/* FTP Archiv-Robot */}
          {activeTab === 'ftp' && (
            <div className="space-y-6">
              <div className="p-4 bg-blue-50 rounded-lg">
                <div className="flex items-start">
                  <QrCode className="h-5 w-5 text-blue-600 mr-3 mt-0.5" />
                  <div className="text-sm text-blue-700">
                    <strong>Archiv-Robot mit integriertem FTP-Server</strong>
                    <p className="mt-1">
                      Der Container enthält einen integrierten FTP-Server. Verbinden Sie sich mit einem
                      FTP-Client, um gescannte PDFs hochzuladen. Der Archiv-Robot ordnet diese automatisch
                      den entsprechenden Verträgen zu (via QR-Code oder Dateiname).
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h3 className="font-medium text-gray-900">FTP Archiv-Robot aktivieren</h3>
                  <p className="text-sm text-gray-500">
                    Automatische Verarbeitung von gescannten Dokumenten
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.ftpEnabled || false}
                    onChange={(e) => updateSetting('ftpEnabled', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
              </div>

              {settings.ftpEnabled && ftpCredentials && (
                <div className="space-y-6">
                  {/* Integrierter FTP-Server Zugangsdaten */}
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <h4 className="font-medium text-green-800 mb-3 flex items-center">
                      <Server className="h-5 w-5 mr-2" />
                      Integrierter FTP-Server - Zugangsdaten
                    </h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <label className="block text-green-700 font-medium mb-1">Host / Server</label>
                        <div className="flex items-center bg-white px-3 py-2 rounded border border-green-300">
                          <span className="flex-1 font-mono">{window.location.hostname}</span>
                          <button
                            onClick={() => copyToClipboard(window.location.hostname, 'Host')}
                            className="p-1 text-green-600 hover:text-green-800"
                          >
                            <Copy className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="block text-green-700 font-medium mb-1">Port</label>
                        <div className="flex items-center bg-white px-3 py-2 rounded border border-green-300">
                          <span className="flex-1 font-mono">{ftpCredentials.port}</span>
                          <button
                            onClick={() => copyToClipboard(String(ftpCredentials.port), 'Port')}
                            className="p-1 text-green-600 hover:text-green-800"
                          >
                            <Copy className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="block text-green-700 font-medium mb-1">Benutzer</label>
                        <div className="flex items-center bg-white px-3 py-2 rounded border border-green-300">
                          <span className="flex-1 font-mono">{ftpCredentials.user}</span>
                          <button
                            onClick={() => copyToClipboard(ftpCredentials.user, 'Benutzer')}
                            className="p-1 text-green-600 hover:text-green-800"
                          >
                            <Copy className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="block text-green-700 font-medium mb-1">Passwort</label>
                        <div className="flex items-center bg-white px-3 py-2 rounded border border-green-300">
                          <span className="flex-1 font-mono">
                            {showPassword ? ftpCredentials.password : '••••••••••'}
                          </span>
                          <button
                            onClick={() => setShowPassword(!showPassword)}
                            className="p-1 text-green-600 hover:text-green-800 mr-1"
                          >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                          <button
                            onClick={() => copyToClipboard(ftpCredentials.password, 'Passwort')}
                            className="p-1 text-green-600 hover:text-green-800"
                          >
                            <Copy className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-green-200">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <label className="block text-green-700 font-medium mb-1">
                            <FolderOpen className="h-4 w-4 inline mr-1" />
                            Inbox-Ordner
                          </label>
                          <div className="flex items-center bg-white px-3 py-2 rounded border border-green-300">
                            <span className="flex-1 font-mono">{ftpCredentials.inboxPath}</span>
                            <button
                              onClick={() => copyToClipboard(ftpCredentials.inboxPath, 'Inbox-Pfad')}
                              className="p-1 text-green-600 hover:text-green-800"
                            >
                              <Copy className="h-4 w-4" />
                            </button>
                          </div>
                          <p className="text-xs text-green-600 mt-1">Hier die gescannten PDFs hochladen</p>
                        </div>
                        <div>
                          <label className="block text-green-700 font-medium mb-1">
                            <FolderOpen className="h-4 w-4 inline mr-1" />
                            Archiv-Ordner
                          </label>
                          <div className="flex items-center bg-white px-3 py-2 rounded border border-green-300">
                            <span className="flex-1 font-mono">{ftpCredentials.archivePath}</span>
                            <button
                              onClick={() => copyToClipboard(ftpCredentials.archivePath, 'Archiv-Pfad')}
                              className="p-1 text-green-600 hover:text-green-800"
                            >
                              <Copy className="h-4 w-4" />
                            </button>
                          </div>
                          <p className="text-xs text-green-600 mt-1">Verarbeitete Dateien</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Scan Intervall */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Scan-Intervall (Minuten)
                    </label>
                    <input
                      type="number"
                      value={settings.ftpScanInterval || 60}
                      onChange={(e) => updateSetting('ftpScanInterval', parseInt(e.target.value))}
                      className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      min={1}
                      max={1440}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Wie oft der FTP-Server abgefragt wird (Standard: 60 Minuten)
                    </p>
                  </div>

                  {/* Manueller Scan */}
                  <div className="flex items-center space-x-4 pt-4 border-t">
                    <button
                      onClick={async () => {
                        try {
                          const result = await settingsApi.scanFtp();
                          setSuccess(`Scan abgeschlossen: ${result.message}`);
                        } catch (err: any) {
                          setError(err.response?.data?.error || 'Scan fehlgeschlagen');
                        }
                      }}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Inbox jetzt scannen
                    </button>
                    <p className="text-sm text-gray-500">
                      Manuell den FTP-Inbox nach neuen Dokumenten durchsuchen
                    </p>
                  </div>
                </div>
              )}

              {/* FTP Client Anleitung */}
              <div className="p-4 bg-gray-50 rounded-lg mt-6">
                <h4 className="font-medium text-gray-900 mb-2">Anleitung: Mit FTP verbinden</h4>
                <ol className="text-sm text-gray-600 list-decimal list-inside space-y-2">
                  <li>Öffnen Sie einen FTP-Client (z.B. FileZilla, WinSCP, Cyberduck)</li>
                  <li>Geben Sie die oben angezeigten Zugangsdaten ein</li>
                  <li>Verbinden Sie sich mit dem Server</li>
                  <li>Laden Sie gescannte PDFs in den <code className="bg-gray-200 px-1 rounded">/inbox</code> Ordner hoch</li>
                  <li>Der Archiv-Robot verarbeitet die Dateien automatisch</li>
                </ol>
              </div>

              {/* Dateinamenskonvention */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Dateinamenskonvention</h4>
                <p className="text-sm text-gray-600">
                  Gescannte Dateien können auf zwei Arten zugeordnet werden:
                </p>
                <ul className="text-sm text-gray-600 list-disc list-inside mt-2 space-y-1">
                  <li><strong>QR-Code:</strong> Der QR-Code auf jeder Seite enthält die Vertrags-ID</li>
                  <li><strong>Dateiname:</strong> Beginnt mit der Vertrags-ID, z.B. <code className="bg-gray-200 px-1 rounded">abc12345-..._page_1.pdf</code></li>
                </ul>
                <p className="text-sm text-gray-600 mt-2">
                  Mehrseitige Scans werden automatisch zusammengefügt, wenn sie dem Muster <code className="bg-gray-200 px-1 rounded">[ID]_page_[Nr].pdf</code> folgen.
                </p>
              </div>
            </div>
          )}

          {/* Erweitert (nur Super-Admin) */}
          {activeTab === 'advanced' && isSuperAdmin && (
            <div className="space-y-6">
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-700">
                <strong>Achtung:</strong> Diese Einstellungen betreffen das gesamte System und sollten mit Vorsicht geändert werden.
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h3 className="font-medium text-gray-900">Multi-Mandanten-Fähigkeit</h3>
                  <p className="text-sm text-gray-500">
                    Separate Datenhaltung für verschiedene Mandanten aktivieren
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.multiTenancyEnabled}
                    onChange={(e) => updateSetting('multiTenancyEnabled', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Save Button */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center"
          >
            <Save className="h-4 w-4 mr-2" />
            {isSaving ? 'Wird gespeichert...' : 'Einstellungen speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}
